import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { format } from 'date-fns';

import { Table } from 'src/components/common/Table';
import { removeEmptyProps } from 'src/helpers/removeEmtyProperties';
import { useDebounce } from 'src/hooks/useDebounce';
import { useRouter } from 'src/hooks/useRouter';
import { routesMap } from 'src/routes';
import { apiRoutesMap } from 'src/shared/config';
import {
  CertificateGroup,
  CertificateSort,
  ProductCodesCell,
  certificateDocTypeLabels,
  certificateStatusLabels,
  certificateStatusColor,
  useGetStaleCertificates,
} from 'src/entities/certificate';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 500;
const MIN_SEARCH_LENGTH = 2;

const STATUS_OPTIONS = [
  { value: 'invalid', label: 'Все невалидные' },
  { value: 'all', label: 'Все статусы' },
  { value: 'valid', label: 'Действует' },
  { value: 'expired', label: 'Истёк' },
  { value: 'suspended', label: 'Приостановлен' },
  { value: 'not_found', label: 'Не найден в реестре' },
  { value: 'unverifiable', label: 'Невозможно проверить' },
  { value: 'unknown', label: 'Неизвестно' },
];

const SORT_OPTIONS: Array<{ value: CertificateSort; label: string }> = [
  { value: 'id', label: 'Сначала новые' },
  { value: 'verified', label: 'Сначала давно не проверявшиеся' },
  { value: 'relevance', label: 'По релевантности' },
];

const formatDay = (value: string | null) =>
  value ? format(new Date(value), 'yyyy-MM-dd') : '—';

const formatWhen = (value: string | null) =>
  value ? format(new Date(value), 'yyyy-MM-dd HH:mm') : '—';

export const CertificatesPage: React.FunctionComponent = () => {
  const { push, query } = useRouter();

  // Пустое значение = «не выбрано»: параметр в API не уходит, дефолт применяет сервер
  // (без поиска — invalid/id, с поиском — all/relevance).
  const statusParam = query.status?.[0] || undefined;
  const sortParam = (query.sort?.[0] as CertificateSort) || undefined;
  const searchParam = String(query.q?.[0] || '').trim();

  const [searchInput, setSearchInput] = useState<string>(searchParam);
  const debouncedSearch = useDebounce<string>(
    searchInput,
    SEARCH_DEBOUNCE_MS,
  ).trim();

  const limit = parseInt(query.limit?.[0] || `${PAGE_SIZE}`, 10) || PAGE_SIZE;
  const offset = parseInt(query.offset?.[0] || '0', 10) || 0;

  const isSearchActive = searchParam.length >= MIN_SEARCH_LENGTH;
  const isSearchTooShort =
    searchInput.trim().length > 0 &&
    searchInput.trim().length < MIN_SEARCH_LENGTH;

  const appliedStatus = statusParam || (isSearchActive ? 'all' : 'invalid');
  const appliedSort: CertificateSort =
    sortParam || (isSearchActive ? 'relevance' : 'id');

  // Адрес — источник истины: переход по ссылке и «назад» обновляют поле ввода.
  useEffect(() => {
    setSearchInput(current =>
      current.trim() === searchParam ? current : searchParam,
    );
  }, [searchParam]);

  // Ввод уходит в адрес после debounce; короткий запрос в API не отправляется.
  // Пока debounce не догнал поле ввода (например, сразу после очистки крестиком),
  // в адрес не пишем — иначе устаревший запрос вернётся обратно.
  useEffect(() => {
    if (debouncedSearch !== searchInput.trim()) return;

    const nextSearch =
      debouncedSearch.length >= MIN_SEARCH_LENGTH ? debouncedSearch : '';

    if (nextSearch === searchParam) return;

    push(
      null,
      removeEmptyProps({ ...query, q: nextSearch || undefined, offset: 0 }),
    );
  }, [debouncedSearch, searchInput, push, query, searchParam]);

  const staleRequest = useGetStaleCertificates({
    variables: {
      q: isSearchActive ? searchParam : undefined,
      status: statusParam,
      sort: sortParam,
      limit,
      offset,
    },
  });

  const total = staleRequest.data?.data?.total || 0;
  const totalBindings = staleRequest.data?.data?.totalBindings || 0;
  const items = staleRequest.data?.data?.items || [];
  const sortValue = staleRequest.data?.data?.sort || sortParam || appliedSort;

  const isSearchEmpty =
    isSearchActive && !staleRequest.isLoading && items.length === 0;

  const clearSearch = () => {
    setSearchInput('');
    push(null, removeEmptyProps({ ...query, q: undefined, offset: 0 }));
  };

  return (
    <Box>
      <Paper
        sx={{ mb: 2, p: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}
      >
        <TextField
          label="Поиск"
          placeholder="Код товара или номер документа"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          sx={{ width: 320 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton disabled={!searchInput} onClick={clearSearch}>
                  <Close />
                </IconButton>
              </InputAdornment>
            ),
          }}
          helperText={
            isSearchTooShort ? `Минимум ${MIN_SEARCH_LENGTH} символа` : ' '
          }
        />
        <TextField
          select
          label="Статус"
          value={appliedStatus}
          onChange={e =>
            push(
              null,
              removeEmptyProps({ ...query, status: e.target.value, offset: 0 }),
            )
          }
          sx={{ width: 280 }}
        >
          {STATUS_OPTIONS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Сортировка"
          value={sortValue}
          onChange={e =>
            push(
              null,
              removeEmptyProps({ ...query, sort: e.target.value, offset: 0 }),
            )
          }
          sx={{ width: 300 }}
        >
          {SORT_OPTIONS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Button
          component="a"
          href={`${apiRoutesMap.certificatesStaleListExport.getRoute()}?status=${appliedStatus}`}
          target="_blank"
          rel="noreferrer"
          variant="outlined"
        >
          Скачать список
        </Button>
      </Paper>
      <Paper>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, px: 2, pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {isSearchActive
              ? `Найдено документов: ${total}`
              : `Документов: ${total}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Привязок «товар → документ»: {totalBindings}
          </Typography>
        </Box>
        {isSearchEmpty && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 1,
              px: 2,
              pb: 2,
            }}
          >
            <Typography variant="subtitle1">
              Ничего не найдено по запросу
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Проверьте код товара или номер документа: «{searchParam}»
            </Typography>
            <Button variant="outlined" size="small" onClick={clearSearch}>
              Очистить поиск
            </Button>
          </Box>
        )}
        <Table
          title={
            isSearchActive
              ? 'Результаты поиска сертификатов'
              : 'Сертификаты «что чинить»'
          }
          withPagination
          loading={staleRequest.isLoading}
          data={items}
          totalCount={total}
          onFetch={staleRequest.mutate}
          onChange={({ page: nextPage, limit: nextLimit }) =>
            push(
              null,
              removeEmptyProps({
                ...query,
                offset: nextPage * nextLimit,
                limit: nextLimit,
              }),
            )
          }
          limit={limit}
          offset={offset}
          extractId={(item: CertificateGroup) => item.id}
          headers={[
            {
              title: 'Тип документа',
              extract: (item: CertificateGroup) =>
                certificateDocTypeLabels[item.docType] || item.docType,
            },
            {
              title: 'Номер',
              extract: (item: CertificateGroup) => (
                <Link href={routesMap.certificate.getRoute(item.id)}>
                  {item.number}
                </Link>
              ),
            },
            {
              title: 'Статус',
              extract: (item: CertificateGroup) => (
                <Box>
                  <Tooltip
                    title={item.statusDetail || ''}
                    disableHoverListener={!item.statusDetail}
                  >
                    <Chip
                      size="small"
                      label={certificateStatusLabels[item.status] || item.status}
                      color={certificateStatusColor(item.status)}
                    />
                  </Tooltip>
                  {item.issueReason && item.actionHint && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      {item.actionHint}
                    </Typography>
                  )}
                </Box>
              ),
            },
            {
              title: 'Действует с',
              extract: (item: CertificateGroup) => formatDay(item.validFrom),
            },
            {
              title: 'Действует по',
              extract: (item: CertificateGroup) => formatDay(item.validUntil),
            },
            {
              title: 'Источник',
              extract: (item: CertificateGroup) => item.source,
            },
            {
              title: 'Проверен',
              extract: (item: CertificateGroup) =>
                item.verifiedAt ? formatWhen(item.verifiedAt) : 'не проверялся',
            },
            {
              title: 'Товары',
              extract: (item: CertificateGroup) => (
                <ProductCodesCell certificate={item} />
              ),
            },
          ]}
        />
      </Paper>
    </Box>
  );
};
